import React, { useMemo, useCallback } from 'react';
import { Grid, Box } from '@mui/material';
import PlaceDetails from '../PlaceDetails/PlaceDetails';

// ============================================
// VirtualizedGrid - Performance optimization for large lists
// ============================================
//
// USAGE:
// - Used ONLY by SmoothScrollList when enableVirtualization=true AND list has >100 items
// - NOT used by SimpleScrollList (the default vertical list in TourParticipant)
// - NOT used by SimpleHorizontalList
//
// PURPOSE:
// - Renders only visible items plus buffer to reduce DOM nodes
// - Improves performance for lists with hundreds of items
// - Uses spacer divs to maintain scroll height
//
// CONFIGURATION:
// - ITEM_HEIGHT should match the actual rendered PlaceDetails card height
// - If cards are different heights, some visual shifting may occur
// - Increase BUFFER_SIZE if you see blank areas during fast scrolling
//
// TRADE-OFFS:
// - Adds complexity for minimal gain with small lists (<100 items)
// - Fixed height assumption may not match actual card heights
// - TourParticipant uses rolling window (15 items) so virtualization not needed
//
// ============================================

const ITEM_HEIGHT = 300; // Estimated height per item - IMPORTANT: should match PlaceDetails card height
const BUFFER_SIZE = 5; // Number of items to render outside viewport for smooth scrolling

const VirtualizedGrid = ({
  places,
  elRefs,
  childClicked,
  onLocationClick,
  language,
  scrollPosition,
  containerHeight
}) => {
  /**
   * Calculate which items should be visible based on scroll position
   */
  const visibleRange = useMemo(() => {
    if (!containerHeight) {
      return { start: 0, end: Math.min(20, places.length) }; // Default visible items
    }

    // Calculate the scroll offset (positive value)
    const scrollTop = Math.abs(scrollPosition);

    // Calculate start and end indices
    const startIndex = Math.floor(scrollTop / ITEM_HEIGHT);
    const endIndex = Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT);

    // Add buffer for smooth scrolling
    const bufferedStart = Math.max(0, startIndex - BUFFER_SIZE);
    const bufferedEnd = Math.min(places.length, endIndex + BUFFER_SIZE);

    return {
      start: bufferedStart,
      end: bufferedEnd
    };
  }, [scrollPosition, containerHeight, places.length]);

  /**
   * Generate placeholder items to maintain proper spacing
   */
  const renderPlaceholders = useCallback(() => {
    const items = [];

    // Top spacer
    if (visibleRange.start > 0) {
      const topSpacerHeight = visibleRange.start * ITEM_HEIGHT;
      items.push(
        <Box
          key="top-spacer"
          sx={{ height: topSpacerHeight, width: '100%' }}
        />
      );
    }

    // Visible items
    for (let i = visibleRange.start; i < visibleRange.end; i++) {
      if (i >= places.length) break;

      const place = places[i];
      items.push(
        <Grid
          ref={elRefs[i]}
          key={place.event_id || place.id || `place-${i}`}
          item
          xs={12}
        >
          <PlaceDetails
            place={place}
            selected={Number(childClicked) === i}
            refProp={elRefs[i]}
            onLocationClick={onLocationClick}
            language={language}
          />
        </Grid>
      );
    }

    // Bottom spacer
    if (visibleRange.end < places.length) {
      const remainingItems = places.length - visibleRange.end;
      const bottomSpacerHeight = remainingItems * ITEM_HEIGHT;
      items.push(
        <Box
          key="bottom-spacer"
          sx={{ height: bottomSpacerHeight, width: '100%' }}
        />
      );
    }

    return items;
  }, [visibleRange, places, elRefs, childClicked, onLocationClick, language]);

  const items = renderPlaceholders();

  return (
    <Grid container spacing={0} sx={{ minHeight: places.length * ITEM_HEIGHT }}>
      {items}
    </Grid>
  );
};

export default VirtualizedGrid;